import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Pure React toast component without Bootstrap JavaScript dependencies
 * This eliminates DOM manipulation timing issues
 */
const SimpleToast = ({
  id,
  title,
  message,
  type = 'success',
  onClose = null,
  autoHide = true,
  delay = 5000,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    // Wait for closing animation to complete
    setTimeout(() => {
      if (onClose) {
        onClose(id);
      }
    }, 300); // Match CSS transition duration
  }, [onClose, id]);

  useEffect(() => {
    // Show the toast with animation
    setIsVisible(true);

    // Auto-hide functionality
    if (autoHide) {
      const timer = setTimeout(() => {
        handleClose();
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [autoHide, delay, handleClose]);

  const getToastVariant = () => {
    switch (type) {
      case 'success':
        return 'text-bg-success';
      case 'error':
        return 'text-bg-danger';
      case 'warning':
        return 'text-bg-warning';
      case 'info':
        return 'text-bg-info';
      default:
        return 'text-bg-primary';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return '•';
    }
  };

  const renderMessage = () => {
    if (Array.isArray(message)) {
      return message.map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < message.length - 1 && <br />}
        </React.Fragment>
      ));
    }
    return message;
  };

  const toastClasses = [
    'toast',
    getToastVariant(),
    isVisible ? 'show' : '',
    isClosing ? 'hiding' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={toastClasses}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      style={{
        transition: 'all 0.3s ease-in-out',
        opacity: isVisible && !isClosing ? 1 : 0,
        transform: isVisible && !isClosing ? 'translateX(0)' : 'translateX(100%)',
      }}
    >
      <div className="toast-header">
        <span className="me-2">{getIcon()}</span>
        <strong className="me-auto">{title}</strong>
        <button
          type="button"
          className="btn-close"
          onClick={handleClose}
          aria-label="Close"
        ></button>
      </div>
      <div className="toast-body">{renderMessage()}</div>
    </div>
  );
};

SimpleToast.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  onClose: PropTypes.func,
  autoHide: PropTypes.bool,
  delay: PropTypes.number,
};

export default SimpleToast;
